import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';
import AppCrashPage, { AppCrashPageVariant } from './AppCrashPage';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  resetKey?: string | number;
  variant?: AppCrashPageVariant;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('App render crashed', error, errorInfo.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <AppCrashPage
          title="Something went wrong"
          message="This page could not be shown. Try again, go home, or email Fieldporter."
          error={this.state.error}
          variant={this.props.variant ?? 'app'}
          showTryAgain
          onTryAgain={this.handleReset}
          showSupport
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
